import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Center, Loader, Text } from '@mantine/core';
import {
  LoginPage as SharedLoginPage,
  getReturnUrl,
  isSessionPending,
  useAuthStore,
} from '@teletienda/shared-auth-ui';
import { useOAuthConfig } from '@/providers/PlatformBootstrapProvider';

/**
 * The portal's `/login` route — a wrapper over the shared `LoginPage`.
 *
 * A person who is already signed in has nothing to do here. The guard sends people to
 * `/login?returnTo=…`, and it can still do so with a live session if the session was adopted
 * from the environment's cookie a moment ago (BUG-SSO-GUARD-HYDRATION-RACE); a form that stays
 * on screen for someone who is signed in is a dead end. So the route has three states, the
 * same three the session has:
 *
 *  - signed in, profile not loaded yet → a loader (the form must not flash, nothing is decided);
 *  - signed in                         → leave for `returnTo` (a validated, same-site address) or `/`;
 *  - anonymous                         → the form, as before.
 *
 * This lives here and not in the shared `LoginPage`: that component is the public API of a
 * published library with one consumer, which is not a reason to change its behaviour. Changing
 * the account is done by signing out, not by visiting `/login`.
 */
export function LoginRoute() {
  const navigate = useNavigate();
  const { facebookAppId } = useOAuthConfig();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);

  const pending = isSessionPending({ isAuthenticated, user });
  const signedIn = isAuthenticated && !pending;

  // One way out, whichever gets there first: the effect below (a session that was already
  // here) or the form's `onSuccess` (a login just completed — which flips `signedIn` too).
  const left = useRef(false);

  useEffect(() => {
    if (!signedIn || left.current) return;
    left.current = true;
    // `replace`: /login is not a place the Back button should return to.
    navigate(getReturnUrl() || '/', { replace: true });
  }, [signedIn, navigate]);

  if (pending || signedIn) {
    return (
      <Center h="100vh" data-testid="login-route-redirecting">
        <Loader />
      </Center>
    );
  }

  return (
    <SharedLoginPage
      logo={
        <Text
          size="xl"
          fw={700}
          c="brand"
          ta="center"
        >
          TeleTienda
        </Text>
      }
      onSuccess={() => {
        if (left.current) return;
        left.current = true;
        navigate(getReturnUrl() || '/');
      }}
      showSocialLogin
      facebookAppId={facebookAppId}
      // Where the guard was sending the user before it bounced them here. Without it the
      // Telegram redirect lands on `AuthCallbackPage`'s `defaultReturnUrl` — the home page.
      returnUrl={getReturnUrl() ?? undefined}
    />
  );
}
